import React from "react";

import "./App.css";
import { full_data } from "./appsData";
import logo from "./logo.png";

const CATEGORIES = [
  { id: "Tehnic", label: "Tehnic" },
  { id: "Sales", label: "Sales" },
];

const FAVORITES_KEY = "koding.favorites";
const THEME_KEY = "koding.theme";

const hostOf = (link) => {
  if (!link) return "";
  try {
    // host, not hostname: keeps the port for internal consoles
    return new URL(link).host.replace(/^www\./, "");
  } catch {
    return link;
  }
};

const initialsOf = (name) =>
  name
    .split(/[\s.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("");

const APPS = full_data.map((app, index) => ({
  ...app,
  id: `${app.categorie}::${app.name}::${index}`,
  host: hostOf(app.link),
}));

const readFavorites = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(FAVORITES_KEY));
    return Array.isArray(stored) ? stored : [];
  } catch {
    return [];
  }
};

const readTheme = () => {
  const stored = localStorage.getItem(THEME_KEY);
  if (stored === "light" || stored === "dark") return stored;
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

function StarIcon({ filled }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M12 3.6l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 17l-5.2 2.7 1-5.8-4.3-4.1 5.9-.9L12 3.6z"
        fill={filled ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function AppCard({ app, isFavorite, onToggleFavorite }) {
  const disabled = !app.link;

  return (
    <div className={`card${disabled ? " card--disabled" : ""}`}>
      <a
        className="card__link"
        href={app.link || undefined}
        target="_blank"
        rel="noreferrer"
        aria-disabled={disabled}
        onClick={(event) => {
          if (disabled) event.preventDefault();
        }}
      >
        <span className="card__logo">
          {app.logo ? (
            <img src={app.logo} alt="" width="32" height="32" decoding="async" />
          ) : (
            <span className="card__initials">{initialsOf(app.name)}</span>
          )}
        </span>
        <span className="card__text">
          <span className="card__name">{app.name}</span>
          <span className="card__host">
            {disabled ? "link indisponibil" : app.host}
          </span>
        </span>
        <svg className="card__go" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M7 17L17 7M17 7H9M17 7v8"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </a>
      <button
        type="button"
        className={`card__star${isFavorite ? " card__star--on" : ""}`}
        onClick={() => onToggleFavorite(app.id)}
        title={isFavorite ? "Scoate din favorite" : "Adaugă la favorite"}
        aria-label={isFavorite ? "Scoate din favorite" : "Adaugă la favorite"}
        aria-pressed={isFavorite}
      >
        <StarIcon filled={isFavorite} />
      </button>
    </div>
  );
}

function Section({ title, count, children }) {
  return (
    <section className="section">
      <h2 className="section__title">
        {title}
        <span className="section__count">{count}</span>
      </h2>
      <div className="grid">{children}</div>
    </section>
  );
}

export default function App() {
  const [query, setQuery] = React.useState("");
  const [category, setCategory] = React.useState("all");
  const [favorites, setFavorites] = React.useState(readFavorites);
  const [theme, setTheme] = React.useState(readTheme);
  const searchRef = React.useRef(null);

  React.useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  React.useEffect(() => {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
  }, [favorites]);

  React.useEffect(() => {
    const onKeyDown = (event) => {
      const input = searchRef.current;
      const inSearch = document.activeElement === input;

      if (event.key === "Escape") {
        setQuery("");
        if (inSearch) input.blur();
        return;
      }
      if (
        (event.key === "k" || event.key === "K") &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault();
        if (input) input.focus();
        return;
      }
      if (event.key === "/" && !inSearch) {
        event.preventDefault();
        if (input) input.focus();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const toggleFavorite = React.useCallback((id) => {
    setFavorites((current) =>
      current.includes(id)
        ? current.filter((favId) => favId !== id)
        : [...current, id]
    );
  }, []);

  const search = query.trim().toLowerCase();

  const visible = React.useMemo(
    () =>
      APPS.filter((app) => {
        if (category !== "all" && app.categorie !== category) return false;
        if (!search) return true;
        return (
          app.name.toLowerCase().includes(search) ||
          app.host.toLowerCase().includes(search) ||
          app.categorie.toLowerCase().includes(search)
        );
      }),
    [category, search]
  );

  const favoriteApps = visible.filter((app) => favorites.includes(app.id));
  const groups = CATEGORIES.map((cat) => ({
    ...cat,
    items: visible.filter((app) => app.categorie === cat.id),
  })).filter((group) => group.items.length > 0);

  const openFirstResult = (event) => {
    event.preventDefault();
    const first = visible.find((app) => app.link);
    if (first) window.open(first.link, "_blank", "noreferrer");
  };

  return (
    <div className="app">
      <header className="topbar">
        <div className="topbar__inner">
          <div className="brand">
            <img src={logo} alt="Koding" className="brand__logo" />
            <span className="brand__label">Internal Apps</span>
          </div>

          <form className="search" onSubmit={openFirstResult} role="search">
            <svg
              className="search__icon"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                d="M11 18a7 7 0 100-14 7 7 0 000 14zm5.5-1.5L21 21"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
              />
            </svg>
            <input
              ref={searchRef}
              className="search__input"
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Caută aplicație sau domeniu…"
              aria-label="Caută aplicație"
            />
            {query ? (
              <button
                type="button"
                className="search__clear"
                onClick={() => setQuery("")}
                aria-label="Șterge căutarea"
              >
                ×
              </button>
            ) : (
              <kbd className="search__kbd">/</kbd>
            )}
          </form>

          <button
            type="button"
            className="theme-toggle"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            title={theme === "dark" ? "Comută pe light" : "Comută pe dark"}
            aria-label={theme === "dark" ? "Comută pe light" : "Comută pe dark"}
          >
            {theme === "dark" ? "☀" : "☾"}
          </button>
        </div>

        <nav className="filters" aria-label="Filtrare pe categorii">
          <button
            type="button"
            className={`chip${category === "all" ? " chip--active" : ""}`}
            onClick={() => setCategory("all")}
          >
            Toate
            <span className="chip__count">{APPS.length}</span>
          </button>
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              type="button"
              className={`chip${category === cat.id ? " chip--active" : ""}`}
              onClick={() => setCategory(cat.id)}
            >
              {cat.label}
              <span className="chip__count">
                {APPS.filter((app) => app.categorie === cat.id).length}
              </span>
            </button>
          ))}
          {search && (
            <span className="filters__hint">
              {visible.length} rezultate pentru „{query.trim()}”
            </span>
          )}
        </nav>
      </header>

      <main className="content">
        {visible.length === 0 ? (
          <div className="empty">
            <p className="empty__title">Nicio aplicație găsită</p>
            <p className="empty__text">
              Încearcă alt termen sau șterge filtrul activ.
            </p>
            <button
              type="button"
              className="empty__reset"
              onClick={() => {
                setQuery("");
                setCategory("all");
              }}
            >
              Resetează
            </button>
          </div>
        ) : (
          <>
            {favoriteApps.length > 0 && (
              <Section title="Favorite" count={favoriteApps.length}>
                {favoriteApps.map((app) => (
                  <AppCard
                    key={`fav-${app.id}`}
                    app={app}
                    isFavorite
                    onToggleFavorite={toggleFavorite}
                  />
                ))}
              </Section>
            )}

            {groups.map((group) => (
              <Section
                key={group.id}
                title={group.label}
                count={group.items.length}
              >
                {group.items.map((app) => (
                  <AppCard
                    key={app.id}
                    app={app}
                    isFavorite={favorites.includes(app.id)}
                    onToggleFavorite={toggleFavorite}
                  />
                ))}
              </Section>
            ))}
          </>
        )}
      </main>

      <footer className="footer">
        Koding Technology · <kbd>/</kbd> caută · <kbd>Enter</kbd> deschide primul
        rezultat · <kbd>Esc</kbd> resetează
      </footer>
    </div>
  );
}
